import React, { useEffect, useState } from 'react';
import { ENTITY } from '@/lib/entity';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const ORIGIN = 'https://gp-cap.com';
const PAGE_URL = `${ORIGIN}/capital-solutions`;
const LOGO_URL = `${ORIGIN}/gp-capital-stacked-frame.svg`;

const PAGE_NAME = 'Capital Solutions — GP Capital';
const PAGE_DESCRIPTION =
  'Private capital across the full structure — senior debt, bridge, construction, mezzanine, preferred equity and special-situations capital — sourced through GP Capital\'s curated institutional network of non-bank lenders, credit funds and family offices.';

const SERVICE_TYPES = [
  'Senior Debt', 'Bridge Finance', 'Construction Finance', 'Mezzanine Capital',
  'Preferred Equity', 'Restructuring Capital', 'Corporate Growth Capital', 'Special-Situations Capital',
];

const ORG_NODE = {
  '@type': 'FinancialService',
  '@id': `${ORIGIN}#organization`,
  name: ENTITY.brand,
  legalName: ENTITY.legalName,
  url: ORIGIN,
  logo: LOGO_URL,
  email: ENTITY.contact.general,
  taxID: `ABN ${ENTITY.abn}`,
  identifier: { '@type': 'PropertyValue', propertyID: 'ACN', value: ENTITY.acn },
  sameAs: [ENTITY.social.linkedin, ENTITY.social.facebook].filter(Boolean),
  areaServed: [
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'Place', name: 'Asia-Pacific' },
  ],
};

/**
 * SeoJsonLd for /capital-solutions.
 *
 * Emits two JSON-LD blocks:
 *   1. A `FinancialService` describing the page, linked to the org node,
 *      with `hasOfferCatalog → OfferCatalog → Offer[]` enumerating the
 *      eight capital solutions.
 *   2. An `ItemList` of curated CreativeWork items derived from the Phase 61
 *      public feed (`/api/public/selected-transactions`). Only the safe
 *      whitelisted fields are used — no internal metadata.
 *
 * If the public feed fails or returns empty, the ItemList block is omitted
 * entirely (no empty schema markers) and the page-level block still renders.
 */
export const CapitalSolutionsJsonLd = () => {
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/selected-transactions?limit=24`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (cancelled) return;
        if (Array.isArray(d?.items)) setTransactions(d.items);
      })
      .catch(() => { /* swallow — emit page-only schema as fallback */ });
    return () => { cancelled = true; };
  }, []);

  const pageNode = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    '@id': `${PAGE_URL}#service`,
    name: 'GP Capital — Capital Solutions',
    description: PAGE_DESCRIPTION,
    url: PAGE_URL,
    provider: ORG_NODE,
    areaServed: ORG_NODE.areaServed,
    serviceType: SERVICE_TYPES,
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Institutional borrowers, sponsors, family offices and non-bank lenders',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Capital Solutions',
      itemListElement: SERVICE_TYPES.map((s) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: s },
        category: 'Private credit / structured finance',
      })),
    },
    isPartOf: {
      '@type': 'WebPage',
      '@id': `${PAGE_URL}#webpage`,
      url: PAGE_URL,
      name: PAGE_NAME,
      description: PAGE_DESCRIPTION,
      inLanguage: 'en-AU',
      isPartOf: { '@type': 'WebSite', '@id': `${ORIGIN}#website`, url: ORIGIN, name: ENTITY.brand },
    },
  };

  // Build the ItemList only when we have real entries from the API.
  // Anything else (fetch failure, empty list, partial bundle) → skip.
  const itemListNode = transactions.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${PAGE_URL}#selected-transactions`,
        name: 'Selected Transactions — Representative Mandates',
        description:
          'Anonymised representative mandates executed or introduced through the GP Capital platform. Borrower, lender, sponsor and counterparty identities are intentionally omitted; structures, jurisdictions and size bands are illustrative of the mandate envelope.',
        numberOfItems: transactions.length,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        itemListElement: transactions.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'CreativeWork',
            '@id': `${PAGE_URL}#st-${(t.code || '').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
            name: t.code,
            headline: `${t.structure} — ${t.sizeBand}`,
            abstract: t.outcome,
            about: [t.sector, t.structure].filter(Boolean),
            keywords: [t.sector, t.structure, t.sizeBand].filter(Boolean).join(', '),
            contentLocation: { '@type': 'Place', name: t.jurisdiction },
            creator: { '@id': `${ORIGIN}#organization` },
            publisher: { '@id': `${ORIGIN}#organization` },
            isPartOf: { '@id': `${PAGE_URL}#service` },
            isAccessibleForFree: true,
            inLanguage: 'en-AU',
            license: `${ORIGIN}/legal/terms`,
          },
        })),
      }
    : null;

  return (
    <>
      <script
        type="application/ld+json"
        data-jsonld="capital-solutions-service"

        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageNode) }}
      />
      {itemListNode && (
        <script
          type="application/ld+json"
          data-jsonld="capital-solutions-selected-transactions"

          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListNode) }}
        />
      )}
    </>
  );
};

export default CapitalSolutionsJsonLd;
